//-- Copyright (c) 2021 TMT Insights Group, LLC
import { TableStatus } from '@/components/_common/Table';
import { Tooltip, Typography } from '@mui/material';
import { getLineageMetaTitle } from '../../_utils/utils';
import AssigneeSelect from '../_common/AssigneeSelect/AssigneeSelect';
import DateTimeDisplay from '../_common/DateTimeDisplay';
import History from '../_common/History/History';
import OverflowMenu from '../_common/OverflowMenu/OverflowMenu';
import PolarisIcon from '../_common/PolarisIcon';
import TaskAction from '../_common/Tasks/TaskAction';
import Notes from '../_common/Notes/Notes';
import TaskType from '../_common/Tasks/TaskType';
import PriorityNumber from './PriorityNumber';
import { OrderJobTask, UserProfile } from '@/redux/slices/slice_types';
import { getAgeFromCreatedAndCompleted } from '@/_utils/date';

export default {
  priority: {
    label: (
      <Tooltip title="Priority">
        <PolarisIcon icon="priority" height={20} style={{ verticalAlign: 'middle' }} />
      </Tooltip>
    ),
    width: 35,
    render: (cell: OrderJobTask['priority']) => {
      return (
        <Tooltip title="Filter by Assignee to assign Priority">
          <div style={{ position: 'relative' }}>
            {/* negative margin and padding to force tooltip hover area to be larger */}
            {Number(cell) ? (
              <PriorityNumber priority={cell} />
            ) : (
              <div style={{ display: 'block', margin: -20, padding: 20 }}>
                <PolarisIcon icon="empty" height={10} style={{ marginLeft: 0 }} />
              </div>
            )}
          </div>
        </Tooltip>
      );
    },
    draggable: (_cell: any, row: OrderJobTask) => row.status !== 'complete' && row.status !== 'cancelled' && !!row.assigned_user
  },
  hid: {
    label: 'Task ID',
    sortField: 'id',
    render: (cell: OrderJobTask['hid']) => <strong>{cell}</strong>
  },
  step: {
    label: 'Task Type',
    sortField: 'step',
    render: (cell: OrderJobTask['step'], row: OrderJobTask) => <TaskType type={cell} prettyName={row.pretty_name} />
  },
  job__due_date: {
    label: 'Due Date',
    minWidth: 100,
    render: (cell: OrderJobTask['job']['due_date']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  created_at_dateformat: {
    sortField: 'created_at',
    label: 'Created',
    minWidth: 120,
    render: (_cell: OrderJobTask['created_at'], row: OrderJobTask) => <DateTimeDisplay>{row.created_at}</DateTimeDisplay>
  },
  updated_at: {
    sortField: 'updated_at',
    label: 'Last Updated',
    minWidth: 120,
    render: (_cell: OrderJobTask['updated_at'], row: OrderJobTask) => <DateTimeDisplay>{row.updated_at}</DateTimeDisplay>
  },
  created_at: {
    label: 'Age',
    width: 120,
    render: (cell: OrderJobTask['created_at'], row: OrderJobTask) => (
      <div style={{ whiteSpace: 'nowrap' }}>{getAgeFromCreatedAndCompleted(row.created_at, row.completed_at)}</div>
    )
  },
  job__title__name: {
    label: 'Title',
    render: (cell: OrderJobTask['job']['title']['name'], row: OrderJobTask) =>
      !cell ? (
        row.job?.title?.name
      ) : (
        <>
          <Typography>
            <strong>{cell}</strong>
          </Typography>
          <Typography variant="caption">{getLineageMetaTitle(row.job.title)}</Typography>
        </>
      )
  },
  status: {
    label: 'Status',
    minWidth: 150,
    render: (cell: OrderJobTask['status'], row: OrderJobTask) => (
      <TableStatus status={cell} statusReason={row.status === 'failed' || row.status === 'in_error' ? row.status_reason : undefined} />
    )
  },
  assigned_user: {
    label: 'Assignee',
    minWidth: 135,
    render: (cell: OrderJobTask['assigned_user'], row: OrderJobTask) => (
      <AssigneeSelect value={cell as UserProfile} id={row.id} assigns="user" priority={row.priority} />
    ),
    disableSort: true
  },
  notes: {
    label: 'Notes',
    render: (_cell: any, row: OrderJobTask) => (
      <div style={{ width: 40, textAlign: 'center' }}>
        <Notes data={row} />
      </div>
    ),
    disableSort: true
  },
  action: {
    label: 'Action',
    disableSort: true,
    width: 75,
    render: (_cell: any, row: OrderJobTask) => <TaskAction task={row} />
  },
  overflow: {
    label: '',
    isOverflow: true,
    render: (_cell: any, row: OrderJobTask) => (
      <OverflowMenu>
        <History data={row} />
      </OverflowMenu>
    )
  }
};
